<script setup lang="ts">
  defineOptions({ name: 'EventDetailSpeakerProfile' })

  import { computed, onBeforeUnmount, ref, type PropType } from 'vue'

  import { useI18n } from 'vue-i18n'

  import {
    ArrowLeftIcon,
    CalendarPlus01Icon,
    CheckCircleIcon,
    Copy01Icon,
    Globe01Icon,
    MarkerPin01Icon,
  } from '@gohighlevel/ghl-icons/24/outline'
  import { InstagramIcon, LinkedinIcon, TwitterDefaultIcon, YoutubeDefaultIcon } from '@gohighlevel/ghl-icons/24/misc'

  import { createElementId } from '@common/core-layer/utils'
  import { isExternalHttpsRedirect } from '@common/core-layer/utils/external-redirect.utils'
  import OptimisedImage from '@common/media-layer/components/OptimisedImage.vue'

  import CPAvatar from '@ui/components/CPAvatar.vue'
  import CPButton from '@ui/components/CPButton.vue'
  import CPDivider from '@ui/components/CPDivider.vue'
  import CPTag from '@ui/components/CPTag.vue'
  import CPText from '@ui/components/CPText.vue'

  import { speakerLinkKind, speakerLinkLabel } from '../utils/speaker-link'

  import type { EventDetailPerson, EventDetailPersonLink, EventDetailSpeakerSession } from '../types/event-detail'
  import type { EventsHubResourceState } from '../types/events-hub'

  const props = defineProps({
    speaker: {
      type: Object as PropType<EventDetailPerson>,
      required: true,
    },
    bio: {
      type: String,
      default: '',
    },
    links: {
      type: Array as PropType<readonly EventDetailPersonLink[]>,
      default: () => [],
    },
    sessions: {
      type: Array as PropType<readonly EventDetailSpeakerSession[]>,
      default: () => [],
    },
    state: {
      type: String as PropType<EventsHubResourceState>,
      default: 'empty',
    },
    idPrefix: {
      type: String,
      default: 'event-detail',
    },
  })

  const emit = defineEmits<{
    closeSpeaker: []
    addToCalendar: [sessionId: string]
  }>()

  const { t } = useI18n()

  const COPY_RESET_MS = 2000
  const copied = ref(false)
  const copyTimeout = ref<ReturnType<typeof setTimeout> | null>(null)

  const bioCopy = computed(() => props.bio.trim() || props.speaker.bio?.trim() || '')
  const followLinks = computed(() => props.links.filter(link => isExternalHttpsRedirect(link.url)))
  const topicTags = computed(() => (props.speaker.tags ?? []).filter(tag => tag.trim()))
  const isLoading = computed(() => props.state === 'loading')
  const isError = computed(() => props.state === 'error')
  const copyLabel = computed(() =>
    copied.value ? t('clientportal.events.detail.speakersCopied') : t('clientportal.events.detail.speakersCopyLink')
  )
  const sessionCountLabel = computed(() =>
    props.sessions.length === 1
      ? t('clientportal.events.detail.speakersSessionCountOne')
      : t('clientportal.events.detail.speakersSessionCountMany', { count: props.sessions.length })
  )

  const sessionGroups = computed(() => {
    const groups: { dayLabel: string; sessions: EventDetailSpeakerSession[] }[] = []
    const indexByDay = new Map<string, number>()
    for (const session of props.sessions) {
      const dayLabel = session.dayLabel?.trim() ?? ''
      const existing = indexByDay.get(dayLabel)
      if (existing === undefined) {
        indexByDay.set(dayLabel, groups.length)
        groups.push({ dayLabel, sessions: [session] })
        continue
      }
      groups[existing]?.sessions.push(session)
    }
    return groups
  })

  const canAddToCalendar = (session: EventDetailSpeakerSession) => Boolean(session.startAt && session.endAt && session.timeZone)

  const sessionDayId = (dayLabel: string) =>
    createElementId(
      props.idPrefix,
      'speaker-profile-day',
      dayLabel
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'undated'
    )

  const linkIcon = (link: EventDetailPersonLink) => {
    switch (speakerLinkKind(link)) {
      case 'linkedin':
        return LinkedinIcon
      case 'x':
        return TwitterDefaultIcon
      case 'instagram':
        return InstagramIcon
      case 'youtube':
        return YoutubeDefaultIcon
      default:
        return Globe01Icon
    }
  }

  const copySpeakerLink = async () => {
    if (typeof window === 'undefined') return
    if (!navigator.clipboard?.writeText) {
      console.error('[EventDetailSpeakerProfile] Clipboard is unavailable')
      return
    }
    try {
      await navigator.clipboard.writeText(window.location.href)
      copied.value = true
      if (copyTimeout.value) clearTimeout(copyTimeout.value)
      copyTimeout.value = setTimeout(() => {
        copied.value = false
        copyTimeout.value = null
      }, COPY_RESET_MS)
    } catch (error) {
      console.error('[EventDetailSpeakerProfile] Failed to copy speaker link:', error)
    }
  }

  onBeforeUnmount(() => {
    if (copyTimeout.value) clearTimeout(copyTimeout.value)
  })
</script>

<template>
  <div
    :id="createElementId(idPrefix, 'speaker-profile', 'root')"
    class="mx-auto flex w-full max-w-[82.5rem] flex-col gap-5 px-4 py-6 md:px-6"
  >
    <CPButton
      :id="createElementId(idPrefix, 'speaker-profile', 'back')"
      type="text"
      color="secondary"
      size="sm"
      class="self-start"
      @click="emit('closeSpeaker')"
    >
      <template #iconLeft>
        <ArrowLeftIcon class="h-4 w-4" aria-hidden="true" />
      </template>
      {{ t('clientportal.events.detail.speakersAll') }}
    </CPButton>

    <section
      :id="createElementId(idPrefix, 'speaker-profile', 'section')"
      class="flex flex-col overflow-hidden rounded-xl border border-solid border-[var(--cp-border-200)] bg-background shadow-sm"
    >
      <div
        :id="createElementId(idPrefix, 'speaker-profile', 'identity')"
        class="flex flex-col gap-8 p-6 md:flex-row md:flex-wrap md:items-start md:gap-8 md:p-8"
      >
        <div
          :id="createElementId(idPrefix, 'speaker-profile', 'photo-wrap')"
          class="aspect-square h-auto w-full max-w-[18.75rem] shrink-0 overflow-hidden rounded-xl bg-[var(--cp-primary-100)] md:h-[18.75rem] md:w-[18.75rem]"
        >
          <OptimisedImage
            v-if="speaker.imageUrl"
            :id="createElementId(idPrefix, 'speaker-profile', 'photo')"
            :src="speaker.imageUrl"
            :alt="speaker.name"
            source="client-portal"
            :use-raw-url="true"
            class="h-full w-full"
            img-class="h-full w-full object-cover object-[center_8%]"
            fallback-aspect="aspect-square"
          />
          <div v-else class="flex h-full w-full items-center justify-center">
            <CPAvatar :title="speaker.name" :size="300" shape="square" source="client-portal" class="shrink-0" />
          </div>
        </div>

        <div class="flex min-w-0 flex-1 flex-col gap-3.5 md:min-w-[17.5rem]">
          <div class="flex flex-col gap-1.5">
            <CPTag
              v-if="speaker.tag"
              :id="createElementId(idPrefix, 'speaker-profile', 'tag')"
              size="xs"
              color="purple"
              :round="true"
              :bordered="false"
              :interactive="false"
              class="self-start"
            >
              <CPText size="xs" weight="bold" class="uppercase tracking-[0.12em]">{{ speaker.tag }}</CPText>
            </CPTag>
            <CPText
              :id="createElementId(idPrefix, 'speaker-profile', 'name')"
              type="display"
              size="sm"
              weight="bold"
              html-tag="h2"
              class="text-pretty tracking-[-0.02em] text-body"
            >
              {{ speaker.name }}
            </CPText>
            <CPText
              v-if="speaker.role"
              :id="createElementId(idPrefix, 'speaker-profile', 'role')"
              size="lg"
              weight="normal"
              html-tag="div"
              class="text-600"
            >
              {{ speaker.role }}
            </CPText>
            <CPText
              v-if="speaker.tagline"
              :id="createElementId(idPrefix, 'speaker-profile', 'tagline')"
              size="md"
              weight="normal"
              html-tag="div"
              class="text-pretty text-400"
            >
              {{ speaker.tagline }}
            </CPText>
            <div
              v-if="speaker.location"
              :id="createElementId(idPrefix, 'speaker-profile', 'location')"
              class="flex items-center gap-1.5 pt-0.5 text-500"
            >
              <MarkerPin01Icon class="h-4 w-4 shrink-0" aria-hidden="true" />
              <CPText size="sm" weight="normal">{{ speaker.location }}</CPText>
            </div>
          </div>
          <div v-if="topicTags.length > 0" :id="createElementId(idPrefix, 'speaker-profile', 'topics')" class="flex flex-wrap gap-2">
            <CPTag
              v-for="topic in topicTags"
              :id="createElementId(idPrefix, 'speaker-profile-topic', topic)"
              :key="topic"
              size="xs"
              color="primary"
              :round="true"
              :bordered="false"
              :interactive="false"
            >
              <CPText size="xs" weight="semibold">{{ topic }}</CPText>
            </CPTag>
          </div>
          <CPText
            v-if="bioCopy"
            :id="createElementId(idPrefix, 'speaker-profile', 'bio')"
            size="md"
            weight="normal"
            html-tag="div"
            class="text-pretty text-600"
          >
            {{ bioCopy }}
          </CPText>

          <div class="flex flex-col gap-2.5 pt-1">
            <div v-if="followLinks.length > 0" class="flex flex-wrap items-center gap-3">
              <CPText size="sm" weight="semibold" html-tag="span" class="w-[3.25rem] shrink-0 text-500">
                {{ t('clientportal.events.detail.speakersFollow') }}
              </CPText>
              <div :id="createElementId(idPrefix, 'speaker-profile', 'follow')" class="flex flex-wrap items-center gap-2">
                <a
                  v-for="link in followLinks"
                  :id="createElementId(idPrefix, 'speaker-profile-link', `${speaker.id}-${speakerLinkKind(link)}`)"
                  :key="`${link.title}-${link.url}`"
                  :href="link.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--cp-surface-100)] text-700"
                  :aria-label="t('clientportal.events.detail.speakersFollowAria', { name: speaker.name, network: speakerLinkLabel(link) })"
                >
                  <component :is="linkIcon(link)" class="h-4 w-4 shrink-0" aria-hidden="true" />
                </a>
              </div>
            </div>
            <div :id="createElementId(idPrefix, 'speaker-profile', 'share')" class="flex flex-wrap items-center gap-3">
              <CPText size="sm" weight="semibold" html-tag="span" class="w-[3.25rem] shrink-0 text-500">
                {{ t('clientportal.events.detail.speakersShare') }}
              </CPText>
              <CPButton
                :id="createElementId(idPrefix, 'speaker-profile', 'copy-link')"
                type="secondary"
                color="secondary"
                size="sm"
                class="self-start"
                :aria-label="t('clientportal.events.detail.speakersCopyLinkAria', { name: speaker.name })"
                @click="copySpeakerLink"
              >
                <template #iconLeft>
                  <component :is="copied ? CheckCircleIcon : Copy01Icon" class="h-4 w-4" aria-hidden="true" />
                </template>
                {{ copyLabel }}
              </CPButton>
            </div>
          </div>
        </div>
      </div>

      <CPDivider />

      <div
        :id="createElementId(idPrefix, 'speaker-profile', 'sessions')"
        class="flex flex-col gap-3 border-t border-solid border-[var(--cp-border-200)] px-6 py-6 md:px-8 md:pb-7"
      >
        <div class="flex flex-wrap items-baseline gap-2.5">
          <CPText
            :id="createElementId(idPrefix, 'speaker-profile', 'sessions-title')"
            size="4xl"
            weight="semibold"
            html-tag="h3"
            class="text-body"
          >
            {{ t('clientportal.events.detail.speakersSessionsTitle') }}
          </CPText>
          <CPText
            v-if="sessions.length > 0"
            :id="createElementId(idPrefix, 'speaker-profile', 'sessions-count')"
            size="xl"
            weight="normal"
            class="text-500"
          >
            {{ sessionCountLabel }}
          </CPText>
        </div>

        <CPText v-if="isLoading" size="md" weight="normal" class="py-4 text-500">
          {{ t('clientportal.events.detail.speakersSessionsLoading') }}
        </CPText>
        <CPText v-else-if="isError" size="md" weight="normal" class="py-4 text-500">
          {{ t('clientportal.events.detail.speakersSessionsError') }}
        </CPText>
        <CPText v-else-if="sessions.length === 0" size="md" weight="normal" class="py-2 text-500">
          {{ t('clientportal.events.detail.speakersNotSpeaking') }}
        </CPText>
        <div v-else class="flex flex-col gap-4">
          <div
            v-for="group in sessionGroups"
            :id="sessionDayId(group.dayLabel)"
            :key="group.dayLabel || 'undated'"
            class="flex flex-col gap-2.5 pt-1.5"
          >
            <CPText
              v-if="group.dayLabel"
              size="xs"
              weight="bold"
              html-tag="h4"
              class="uppercase tracking-[0.08em] text-[var(--cp-font-400)]"
            >
              {{ group.dayLabel }}
            </CPText>
            <div
              v-for="session in group.sessions"
              :id="createElementId(idPrefix, 'speaker-profile-session', session.id)"
              :key="session.id"
              class="relative flex flex-col gap-3 rounded-[10px] border border-solid border-[var(--cp-border-200)] bg-background p-4 pr-14 sm:flex-row sm:items-start sm:gap-4 sm:pr-4"
            >
              <div :id="createElementId(idPrefix, 'speaker-profile-session-meta', session.id)" class="w-full shrink-0 sm:w-[9.75rem]">
                <CPText
                  v-if="session.timeLabel"
                  size="md"
                  weight="semibold"
                  html-tag="div"
                  class="whitespace-nowrap tabular-nums text-body"
                >
                  {{ session.timeLabel }}
                </CPText>
                <span v-if="session.venue" class="mt-0.5 inline-flex min-w-0 items-center gap-1.5 text-500">
                  <MarkerPin01Icon class="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  <CPText size="sm" weight="normal" class="truncate">{{ session.venue }}</CPText>
                </span>
              </div>
              <div :id="createElementId(idPrefix, 'speaker-profile-session-body', session.id)" class="min-w-0 flex-1">
                <div class="flex flex-wrap items-center gap-2">
                  <CPText size="lg" weight="semibold" html-tag="div" class="text-pretty text-body">
                    {{ session.title }}
                  </CPText>
                  <CPTag
                    v-if="session.tag"
                    :id="createElementId(idPrefix, 'speaker-profile-session-tag', session.id)"
                    size="xs"
                    color="primary"
                    :round="true"
                    :bordered="false"
                    :interactive="false"
                  >
                    <CPText size="xs" weight="semibold">{{ session.tag }}</CPText>
                  </CPTag>
                </div>
                <CPText
                  v-if="session.description"
                  :id="createElementId(idPrefix, 'speaker-profile-session-description', session.id)"
                  size="md"
                  weight="normal"
                  html-tag="div"
                  class="mt-1.5 max-w-[70ch] text-pretty text-500"
                >
                  {{ session.description }}
                </CPText>
              </div>
              <CPButton
                v-if="canAddToCalendar(session)"
                :id="createElementId(idPrefix, 'speaker-profile-calendar', session.id)"
                type="secondary"
                color="secondary"
                size="xs"
                class="absolute right-4 top-4 shrink-0 self-start sm:static"
                :aria-label="t('clientportal.events.detail.agendaAddToCalendar')"
                @click="emit('addToCalendar', session.id)"
              >
                <CalendarPlus01Icon class="h-3.5 w-3.5" aria-hidden="true" />
              </CPButton>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
